/*
 * email-capture.js — the pop-up, the phone banner, the desktop tab and its panel.
 *
 * Six surfaces, one per Figma frame (kit bItSyC5DaQSvEwzd1L774C, "Pop-ups · Website"):
 *
 *   modal   DESKTOP 1 / 2   the split card, exit intent only
 *   sheet   MOBILE 2 / 3    bottom sheet, only after someone taps Join
 *   banner  MOBILE 1        the thin line under the header
 *   tab     DESKTOP 3       the disc bottom left, after the card is closed
 *   panel   DESKTOP 4       what the disc opens. small, blocks nothing
 *   inline  MOBILE 4        after a phone dismissal. in the page, floating over nothing
 *
 * Three rules decide almost everything in here, and all three came from
 * measuring rather than guessing:
 *
 * 1. NOTHING OPENS BY ITSELF ON A PHONE. Google penalises an interstitial shown
 *    on arrival from search or while someone is browsing, and explicitly allows a
 *    banner that uses a reasonable amount of space and is easily dismissed. So a
 *    phone gets the banner, and the sheet opens only on a tap. A sheet someone
 *    asked for is not an interstitial.
 *
 * 2. NOTHING AT ALL ON A PRODUCT PAGE ON A PHONE. `.sticky-atc` owns the bottom
 *    68px there, the Shopify Inbox launcher sits at 20-66px on the right, and
 *    back-to-top at 88px above it. There is no room, and someone on a product
 *    page is close to buying.
 *
 * 3. THESE ARE PLAIN FORMS, NOT {% form 'customer' %}. Shopify attaches its
 *    hCaptcha bot protection to customer forms and, after the challenge, calls
 *    form.submit() itself. A programmatic submit fires no event and cannot be
 *    prevented, so signing up bounced through /challenge back to
 *    ?customer_posted=true: the confirmation flashed, the page reloaded, and
 *    the pop-up came back at step one. The no-JS fallback it bought us was
 *    worth nothing anyway, because without JS the pop-up never opens at all.
 *
 * Klaviyo takes the address through its client subscription endpoint, which
 * records explicit consent with a timestamp and a named source. That endpoint
 * takes the PUBLIC company id; the private key never touches the browser.
 */
(function () {
  'use strict';

  var KEY = 'aykah_ec';                 // { dismissed: epoch, joined: bool, anon: id }
  var MIN_SUBMIT_MS = 500;              // the spinner reads as a state, not a flicker
  var TIMEOUT_MS = 15000;
  var MOBILE = '(max-width: 767px)';

  // EVERY REFERENCE IS RE-RESOLVED, NEVER CAPTURED ONCE.
  //
  // The theme editor replaces the whole section's HTML on each setting change.
  // Anything captured at load time is then pointing at detached nodes, which is
  // exactly how a pop-up ends up on screen with dead buttons and a dead form.
  // So bind() re-reads the DOM, every listener lives on `document`, and
  // shopify:section:load re-runs the lot.
  var root, card, scrim, banner, dock, tab, panel, inline;
  var isProduct, days, resetMs, delayMs, companyId, listId, IN_EDITOR;
  var mobileDelayMs, mobileScrollPct, mobileTrigger, mobileSurface;
  var disarmBanner = null;          // tears down whatever the last boot armed

  var isMobile = function () { return window.matchMedia(MOBILE).matches; };
  var wiredEditor = false, wiredExit = false, wiredBreakpoint = false;
  var editorClosed = false;             // the merchant closed it. do not fight them.
  var editorSurface = 'card';           // what they were last looking at
  var editorState = 'idle';             // and which stage of it

  function bind() {
    // Only ever one pop-up per page. A second copy (the section added to a
    // template as well as the site-wide mount) is stripped rather than left to
    // fight the first over ids and focus.
    var roots = document.querySelectorAll('[data-ec-root]');
    if (!roots.length) { root = null; return false; }
    for (var i = 1; i < roots.length; i++) roots[i].parentNode.removeChild(roots[i]);

    root = roots[0];
    card = root.querySelector('[data-ec-card]');
    scrim = root.querySelector('[data-ec-scrim]');
    // relocate() moves this out of the root, so on the second bind it is no
    // longer a descendant and root.querySelector returns null. Everything
    // keyed off `banner` then silently stops. Same for the inline block.
    banner = root.querySelector('[data-ec-banner]') || document.querySelector('[data-ec-banner]');
    dock = root.querySelector('[data-ec-dock]');
    tab = root.querySelector('[data-ec-tab]');
    panel = root.querySelector('[data-ec-panel]');
    inline = root.querySelector('[data-ec-inline]') || document.querySelector('[data-ec-inline]');

    isProduct = root.getAttribute('data-ec-product') === 'true';
    days = parseInt(root.getAttribute('data-ec-days') || '30', 10);
    resetMs = (parseInt(root.getAttribute('data-ec-reset') || '5', 10) || 0) * 1000;
    delayMs = (parseInt(root.getAttribute('data-ec-delay') || '15', 10) || 0) * 1000;
    // THESE DEFAULTS MUST REPRODUCE THE OLD BEHAVIOUR, NOT THE NEW ONE.
    //
    // This file and the section ship together, but they do not always ARRIVE
    // together: the theme took this asset and kept the previous section, so for
    // an hour the live site ran new script against old markup. The attributes
    // below were absent, the script fell back to its own idea of a good default,
    // and the phone silently changed from "banner, immediately" to "sheet, after
    // ten seconds" with nothing in the editor to control it. It looked broken
    // because nothing appeared, and it was unreachable because the settings that
    // govern it were not there yet.
    //
    // So: absent attribute means the section predates this feature, and the only
    // correct thing to do is behave exactly as that section expects. The new
    // defaults live in the schema, where they belong, and take effect the moment
    // the section itself arrives.
    var configured = root.hasAttribute('data-ec-msurface');
    mobileSurface = !configured ? 'banner'
      : (root.getAttribute('data-ec-msurface') === 'banner' ? 'banner' : 'sheet');
    mobileDelayMs = !configured ? 0
      : (parseInt(root.getAttribute('data-ec-mdelay') || '0', 10) || 0) * 1000;
    mobileScrollPct = !configured ? 0
      : (parseInt(root.getAttribute('data-ec-mscroll') || '0', 10) || 0);
    mobileTrigger = root.getAttribute('data-ec-mtrigger') === 'both' ? 'both' : 'first';
    // The editor re-renders the section on every setting change. Without this
    // a timer from the previous render is still counting down against a node
    // that no longer exists, and the new settings never get a clean run.
    if (disarmBanner) { disarmBanner(); disarmBanner = null; }
    companyId = root.getAttribute('data-ec-company');
    listId = root.getAttribute('data-ec-list');
    IN_EDITOR = root.getAttribute('data-ec-editor') === 'true';

    relocate();
    return true;
  }

  /* ---------- where the two in-flow surfaces actually belong ----------
   *
   * The section is mounted last in theme.liquid, after the footer. Two of the
   * six surfaces are not overlays and have to live somewhere else in the page:
   *
   *   the banner sits under #site-header, which is the only place it pushes the
   *   page down instead of covering it. Left where it renders it is below the
   *   footer, which is why the phone appeared to have no design at all.
   *
   *   the in-page block goes at the end of #main-content, above the footer.
   *
   * Both are moved, not cloned, so there is still exactly one of each and the
   * delegated listeners keep working.
   */
  function relocate() {
    if (banner) {
      var header = document.getElementById('site-header');
      if (header && banner.previousElementSibling !== header) {
        header.parentNode.insertBefore(banner, header.nextSibling);
      }
    }
    if (inline) {
      var main = document.getElementById('main-content');
      if (main && inline.parentNode !== main) main.appendChild(inline);
    }
  }

  /* ---------- memory. a dismissal is a decision, so it sticks ---------- */
  function read() {
    try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch (e) { return {}; }
  }
  function write(patch) {
    try {
      var next = read();
      for (var k in patch) next[k] = patch[k];
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch (e) { /* private mode. the session still behaves, it just forgets. */ }
  }
  var SKEY = 'aykah_ec_session';
  function readSession() {
    try { return JSON.parse(sessionStorage.getItem(SKEY) || '{}'); } catch (e) { return {}; }
  }
  function writeSession(patch) {
    try {
      var next = readSession();
      for (var k in patch) next[k] = patch[k];
      sessionStorage.setItem(SKEY, JSON.stringify(next));
    } catch (e) { /* private mode forgets, and that is fine */ }
  }

  /* ONE LAYER AT A TIME. The flow spec: "If the cart drawer is open, the
   * signup waits." The theme's drawers all lock the page and announce
   * themselves, so ask them rather than guess. */
  function layerOpen() {
    var b = document.body;
    if (!b) return false;
    if (b.classList.contains('cart-drawer-open')) return true;
    if (b.classList.contains('pr-modal-open')) return true;
    if (b.classList.contains('rpw-lb-open')) return true;
    if (b.style.overflow === 'hidden') return true;   // any drawer that locks scroll
    return false;
  }

  function suppressed() {
    var s = read();
    if (s.joined) return true;
    if (!s.dismissed) return false;
    return (Date.now() - s.dismissed) < days * 86400000;
  }

  /* ---------- opening and closing ---------- */
  var lastFocus = null;

  function open(view) {
    if (!root || root.getAttribute('data-ec-view') !== 'hidden') return;
    // One sheet at a time. A second overlay on top of the cart is not a
    // second chance, it is two things asking at once.
    if (!IN_EDITOR && layerOpen()) return;
    closePanel(false);
    lastFocus = document.activeElement;
    root.setAttribute('data-ec-view', view);
    card.setAttribute('aria-hidden', 'false');
    scrim.hidden = false;
    card.hidden = false;
    if (banner) banner.hidden = true;
    if (dock) dock.hidden = true;
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.classList.add('ec-open');
    track(view === 'modal' ? 'Popup Viewed' : 'Popup Sheet Opened');
    var first = card.querySelector('[data-ec-input]');
    if (first && !IN_EDITOR) setTimeout(function () { first.focus(); }, 60);
    document.addEventListener('keydown', onKey);
  }

  function close(remember) {
    if (!root) return;
    var wasSheet = root.getAttribute('data-ec-view') === 'sheet';
    root.setAttribute('data-ec-view', 'hidden');
    card.setAttribute('aria-hidden', 'true');
    scrim.hidden = true;
    card.hidden = true;
    document.documentElement.style.overflow = '';
    document.documentElement.classList.remove('ec-open');
    document.removeEventListener('keydown', onKey);
    if (IN_EDITOR) editorClosed = true;
    // A merchant closing the card in the editor has not dismissed anything.
    if (remember && !IN_EDITOR) { write({ dismissed: Date.now() }); track('Popup Dismissed'); }
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    // Everyone keeps a way back, it just costs nothing. Desktop gets the disc,
    // and the editor always gets it because the disc is part of the design.
    // A phone gets the in-page block, because the bottom of a phone is taken.
    if (isMobile()) { if (!IN_EDITOR && (wasSheet || remember)) showInline(); }
    else if (dock && (IN_EDITOR || (!read().joined && !readSession().tabDismissed))) dock.hidden = false;
  }

  /* DESKTOP 4. The disc opens a small card in place. No scrim, nothing blocked.
   *
   * The motion is a keyframe animation, never a transition, and the flag that
   * runs it is always cleared afterwards. A transition needs a frame to take
   * its start value; when that frame did not arrive the panel sat there at
   * opacity 0, present and invisible. An animation plus a guaranteed cleanup
   * cannot do that: the resting style is fully visible.
   */
  var animTimer = null, animGen = 0;
  function playAnim(el, name, ms, done) {
    if (!el) { if (done) done(); return; }
    clearTimeout(animTimer);
    var gen = ++animGen;                 // anything older than this is stale
    el.setAttribute('data-ec-anim', name);
    var finished = false;
    var end = function () {
      if (finished) return;
      finished = true;
      el.removeEventListener('animationend', end);
      if (gen !== animGen) return;       // a newer animation owns the element
      el.removeAttribute('data-ec-anim');
      if (done) done();
    };
    el.addEventListener('animationend', end);
    // the backstop. setTimeout survives cases where animationend never fires.
    animTimer = setTimeout(end, ms);
  }

  // `hidden` now lags the close by the length of the exit, so anything asking
  // "is the panel up?" has to ask the flag, not the attribute.
  function panelOpen() {
    return !!root && root.getAttribute('data-ec-panel-open') === 'true';
  }

  function openPanel() {
    if (!panel || !root) return;
    panel.hidden = false;
    if (IN_EDITOR) editorSurface = 'panel';
    root.setAttribute('data-ec-panel-open', 'true');
    playAnim(panel, 'in', 700);
    track('Popup Tab Opened');
    var first = panel.querySelector('[data-ec-input]');
    if (first && !IN_EDITOR) setTimeout(function () { first.focus(); }, 60);
    document.addEventListener('keydown', onKey);
  }
  function closePanel(dismissDock) {
    if (!panel || !root) return;
    if (!panel.hidden) {
      root.removeAttribute('data-ec-panel-open');
      // let it fall back towards the disc before it goes
      playAnim(panel, 'out', 400, function () {
        if (root && root.getAttribute('data-ec-panel-open') !== 'true') panel.hidden = true;
      });
    }
    if (IN_EDITOR) { editorClosed = true; return; }
    // Flow step 7: "They can dismiss the tab too. Then it is gone for the rest
    // of the session." Closing the compact form with its X is that dismissal;
    // tapping the disc again only collapses it.
    if (dismissDock && dock) {
      dock.hidden = true;
      writeSession({ tabDismissed: true });
      track('Popup Tab Dismissed');
    }
  }

  function showInline() {
    if (!inline) return;
    if (read().joined) return;
    inline.hidden = false;
  }

  function onKey(e) {
    if (e.key === 'Escape') {
      if (panelOpen()) { closePanel(false); return; }
      close(true);
      return;
    }
    if (e.key !== 'Tab') return;
    var box = panelOpen() ? panel : card;
    if (!box || box.hidden) return;
    var f = [].slice.call(box.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------- what shows, and when ---------- */
  /* ---------- PHONE: when the banner is allowed to arrive ----------
   *
   * It used to be there on load, which is the one moment nobody has decided
   * they like the place yet. Two conditions can now hold it back, each one off
   * at zero: a wait, and a scroll depth. With both set, `mobileTrigger` decides
   * whether either is enough or both have to be true.
   *
   * A page shorter than the viewport has nothing to scroll, so scrolledPct()
   * returns -1 there and the scroll condition can never be met. That is why the
   * setting tells them to keep a wait set: on a short page the wait is the only
   * thing that will ever fire.
   */
  function scrolledPct() {
    var doc = document.documentElement;
    var max = (doc.scrollHeight || 0) - window.innerHeight;
    if (max <= 0) return -1;                       // nothing to scroll
    var y = window.pageYOffset || doc.scrollTop || 0;
    return Math.min(100, Math.max(0, (y / max) * 100));
  }

  function showBannerNow() {
    if (!banner) return;
    banner.hidden = false;
    track('Popup Banner Shown');
  }

  function armMobileEntry() {
    if (!banner) return;

    var wantTime = mobileDelayMs > 0;
    var wantScroll = mobileScrollPct > 0;
    if (!wantTime && !wantScroll) {
      if (mobileSurface === 'banner') showBannerNow(); else open('sheet');
      return;
    }

    var timeMet = false, scrollMet = false, fired = false;
    var timer = null, lastCheck = 0;

    function ready() {
      if (mobileTrigger === 'both') {
        return (!wantTime || timeMet) && (!wantScroll || scrollMet);
      }
      return (wantTime && timeMet) || (wantScroll && scrollMet);
    }

    function teardown() {
      if (timer) { clearTimeout(timer); timer = null; }
      window.removeEventListener('scroll', onScroll);
    }

    function fire() {
      if (fired || !ready()) return;
      fired = true;
      teardown();
      disarmBanner = null;
      // They have had time to join or dismiss in another tab, and the window
      // may have been widened past the phone breakpoint while we waited.
      if (suppressed()) return;
      if (!isMobile()) return;
      if (mobileSurface === 'banner') showBannerNow();
      else open('sheet');
    }

    function onScroll() {
      // Reading scrollHeight forces layout, so not on every scroll event.
      var now = Date.now();
      if (now - lastCheck < 150) return;
      lastCheck = now;
      if (scrolledPct() >= mobileScrollPct) { scrollMet = true; fire(); }
    }

    if (wantTime) timer = setTimeout(function () { timeMet = true; fire(); }, mobileDelayMs);
    if (wantScroll) window.addEventListener('scroll', onScroll, { passive: true });
    disarmBanner = teardown;
  }

  function start() {
    // THE THEME EDITOR. Nothing here runs on the storefront.
    //
    // A pop-up is position:fixed and hidden until something triggers it, so the
    // editor canvas shows an empty slot and there is nothing to judge. In design
    // mode the card is opened on sight, and again whenever the section is
    // selected in the sidebar, so the real thing is always what is being edited.
    //
    // The X used to be unclickable: clicking anywhere inside the section makes
    // Shopify re-select it, shopify:section:select fired, and the card reopened
    // in the same tick. So a close is now remembered, and only a fresh select
    // after a deselect brings it back.
    if (IN_EDITOR) {
      if (dock) dock.hidden = false;      // the disc is part of the design. show it.
      // THE EDITOR RE-RENDERS THE SECTION ON EVERY SETTING CHANGE, and it also
      // fires select when you click inside it. Both used to run open('modal'),
      // which shut whatever you had open and put the card back on top. That is
      // why the disc looked dead: the panel opened and was closed again in the
      // same breath. Restore what they were on instead.
      if (editorState === 'success') root.setAttribute('data-ec-state', 'success');
      if (editorSurface === 'panel') openPanel();
      else if (editorSurface === 'banner') { if (banner) banner.hidden = false; }
      else if (editorSurface === 'inline') { if (inline) inline.hidden = false; }
      else if (!editorClosed) open('modal');
      if (!wiredEditor) {
        wiredEditor = true;
        document.addEventListener('shopify:section:select', function (e) {
          if (!root || !e.target || !e.target.querySelector) return;
          if (!e.target.querySelector('[data-ec-root]')) return;
          if (editorClosed) return;
          // Something of ours is already open. Selecting the section again is
          // not a request to replace it with the card.
          if (panelOpen()) return;
          if (banner && !banner.hidden) return;
          if (card && !card.hidden) return;
          open('modal');
        });
        document.addEventListener('shopify:section:deselect', function (e) {
          if (!root || !e.target || !e.target.querySelector) return;
          if (!e.target.querySelector('[data-ec-root]')) return;
          // close() sets the flag, so clearing it has to come after, or
          // deselecting then reselecting never brings the card back.
          close(false);
          closePanel(false);
          if (banner) banner.hidden = true;
          editorClosed = false;
          editorSurface = 'card';
          editorState = 'idle';
        });
      }
      return;
    }

    if (read().joined) return;
    if (suppressed()) {
      if (isMobile()) { if (!isProduct) showInline(); return; }
      // The modal never returns, but the quiet way in stays, unless they got
      // rid of that too this session.
      if (dock && !readSession().tabDismissed) dock.hidden = false;
      return;
    }

    if (isMobile()) {
      // rule 2: not here at all
      if (isProduct) return;
      // rule 1: a banner, not a sheet. When it turns up is now a setting.
      armMobileEntry();
      return;
    }

    // Flow step 1 on a computer: "Someone browses. Nothing appears." The disc
    // arrives in close(), once the card has actually been seen and dismissed.

    if (!wiredExit) {
      wiredExit = true;
      var armed = false;
      setTimeout(function () { armed = true; }, delayMs);
      document.addEventListener('mouseout', function (e) {
        if (!armed) return;
        if (e.relatedTarget || e.toElement) return;   // still inside the document
        if (e.clientY > 0) return;                     // only through the top edge
        if (suppressed()) return;
        open('modal');
      });
    }

    // A window dragged across 767px while the card is open used to leave a
    // desktop modal wearing the phone stylesheet: squeezed copy, collapsed
    // image band. The view now follows the breakpoint.
    if (!wiredBreakpoint) {
      wiredBreakpoint = true;
      var mq = window.matchMedia(MOBILE);
      var onChange = function () {
        if (!root) return;
        var v = root.getAttribute('data-ec-view');
        if (v === 'hidden') return;
        root.setAttribute('data-ec-view', mq.matches ? 'sheet' : 'modal');
      };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }
  }

  /* ---------- state ---------- */
  function setState(s) {
    if (!root) return;
    if (IN_EDITOR) editorState = s;
    root.setAttribute('data-ec-state', s);
    // The in-page block lives in #main-content, outside the root, so it has to
    // carry the state itself or its confirmation never cross-fades in.
    if (inline) inline.setAttribute('data-ec-state', s);
    if (s !== 'success' || resetMs <= 0) return;
    setTimeout(function () {
      if (!root || root.getAttribute('data-ec-state') !== 'success') return;
      if (panelOpen()) { closePanel(false); return; }
      if (inline && !inline.hidden && card.hidden) return;   // in-page, nothing to close
      close(false);
    }, resetMs);
  }
  function showError(scope, m) {
    var el = scope && scope.querySelector('[data-ec-errmsg]');
    if (el) { el.textContent = m; el.hidden = false; }
  }
  function clearError(scope) {
    var el = scope && scope.querySelector('[data-ec-errmsg]');
    if (el) el.hidden = true;
  }

  /* ---------- Klaviyo ----------
   *
   * Two calls, both to the public client endpoints, both with the company id
   * only. The private key never reaches the browser.
   *
   *   /client/events/        so the pop-up shows up in Klaviyo's reporting at all.
   *                          A custom form gets none of the built-in form
   *                          analytics, so we send our own metrics and they
   *                          become chartable like any other.
   *   /client/subscriptions/ the sign-up itself, with explicit consent.
   *
   * Events need someone to attach to and we have no email until they submit, so
   * every browser gets an anonymous id that persists. Passing the same id on the
   * subscription stitches the earlier views onto the real profile.
   */
  function anonId() {
    var s = read();
    if (s.anon) return s.anon;
    var id = 'ec-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    write({ anon: id });
    return id;
  }

  function track(metric, extra) {
    // A merchant clicking around the theme editor is not a customer. No events.
    if (IN_EDITOR) return;
    if (!window.fetch || !companyId) return;
    var props = { variant: root.getAttribute('data-ec-variant') || 'default',
                  device: isMobile() ? 'mobile' : 'desktop',
                  page_type: isProduct ? 'product' : 'other' };
    for (var k in (extra || {})) props[k] = extra[k];
    try {
      fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST', keepalive: true,
        headers: { 'content-type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({ data: { type: 'event', attributes: {
          properties: props,
          metric: { data: { type: 'metric', attributes: { name: metric } } },
          profile: { data: { type: 'profile', attributes: { anonymous_id: anonId() } } }
        } } })
      }).catch(function () { /* reporting must never break the form */ });
    } catch (e) { /* same */ }
  }

  /* the sign-up itself
   *
   * NO `subscriptions` OBJECT. It reads like it belongs here and it is what
   * the older server-side shape wants, but the CLIENT endpoint rejects it:
   *
   *   400 "'subscriptions' is not a valid field for the resource 'profile'"
   *   pointer /data/attributes/profile/data/attributes/subscriptions
   *
   * The schema check runs before the address is even looked at, so EVERY
   * sign-up failed and every visitor saw "We could not add that address".
   * Consent is implicit in calling this endpoint at all: it is the documented
   * opt-in route and records the timestamp and custom_source itself.
   * Measured against the live API from aykah.ca, not recalled.
   */
  function subscribe(email, source) {
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: source || 'Website pop-up',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  anonymous_id: anonId()
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      })
    });
  }

  /* ---------- wiring. on document, so a re-rendered section stays alive -------
   *
   * The banner and the in-page block are moved out of the root, so the guard
   * has to name them too or their buttons go dead the moment they are relocated.
   */
  function inOurs(el) {
    return el.closest('[data-ec-root]') || el.closest('[data-ec-banner]') || el.closest('[data-ec-inline]');
  }

  document.addEventListener('click', function (e) {
    if (!root || !e.target || !e.target.closest) return;

    if (e.target.closest('[data-ec-preview]')) {
      var what = e.target.closest('[data-ec-preview]').getAttribute('data-ec-preview');
      editorClosed = false;
      editorSurface = (what === 'panel' || what === 'banner' || what === 'inline') ? what : 'card';
      editorState = (what === 'success') ? 'success' : 'idle';
      if (banner) banner.hidden = true;
      if (inline) inline.hidden = true;
      if (what === 'banner') { close(false); closePanel(false); if (banner) banner.hidden = false; return; }
      if (what === 'inline') { close(false); closePanel(false); if (inline) inline.hidden = false; return; }
      if (what === 'panel') { close(false); if (dock) dock.hidden = false; openPanel(); return; }
      closePanel(false);
      setState(what === 'success' ? 'success' : 'idle');
      open(isMobile() ? 'sheet' : 'modal');
      return;
    }

    if (!inOurs(e.target)) return;
    if (e.target.closest('[data-ec-scrim]')) { close(true); return; }
    if (e.target.closest('[data-ec-panelclose]')) { closePanel(true); return; }
    if (e.target.closest('[data-ec-tab]')) {
      if (panelOpen()) closePanel(false); else openPanel();
      return;
    }
    if (e.target.closest('[data-ec-open]')) { open(isMobile() ? 'sheet' : 'modal'); return; }
    if (e.target.closest('[data-ec-close]')) { close(true); return; }
    if (e.target.closest('[data-ec-dismiss]')) {
      if (banner) banner.hidden = true;
      write({ dismissed: Date.now() });
      track('Popup Dismissed');
      showInline();
    }
  });

  // The theme's own drawers announce themselves this way (cart, wishlist and
  // the mobile nav all listen for it). Ours yields like the rest of them.
  document.addEventListener('drawer:request-close', function (e) {
    if (e && e.detail && e.detail.except === 'email-capture') return;
    if (!root) return;
    if (root.getAttribute('data-ec-view') !== 'hidden') close(false);
    closePanel(false);
  });

  // A click anywhere else closes the little panel. It is not modal, so it
  // should behave like every other popover on the site.
  document.addEventListener('pointerdown', function (e) {
    if (!panelOpen()) return;
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('[data-ec-dock]')) return;
    closePanel(false);
  });

  document.addEventListener('submit', function (e) {
    var formEl = e.target;
    if (!formEl || !formEl.classList) return;
    if (!formEl.classList.contains('ec-formel')) return;

    // FIRST, ALWAYS. These are plain forms with no action, so letting one
    // through reloads the page onto itself. Nothing below is allowed to
    // decide otherwise. (They used to be {% form 'customer' %}, which
    // Shopify's hCaptcha script re-submits programmatically after its
    // challenge; that is unstoppable from here, which is why signing up
    // bounced through /challenge and back to ?customer_posted=true.)
    e.preventDefault();
    if (!root || !window.fetch) return;

    // The address comes off the form that was submitted. Three of these exist
    // on a page (card, tab panel, in-page block) and a cached reference to one
    // of them sends the wrong value, or an empty one.
    var field = formEl.querySelector('[data-ec-input]');
    var email = (field && field.value || '').trim();
    if (!email) return;

    var scope = formEl.closest('[data-ec-panel]') || formEl.closest('[data-ec-inline]') || card;
    var source = formEl.closest('[data-ec-panel]') ? 'Website pop-up tab'
               : formEl.closest('[data-ec-inline]') ? 'Website in-page block'
               : 'Website pop-up';

    clearError(scope);
    setState('submitting');
    var started = Date.now();

    var timer = setTimeout(function () { finish(false, 'That took too long. Please try again.'); }, TIMEOUT_MS);
    var settled = false;

    function finish(ok, message) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      var wait = Math.max(0, MIN_SUBMIT_MS - (Date.now() - started));
      setTimeout(function () {
        if (ok) {
          write({ joined: true });   // idempotent; already written on the response
          track('Popup Submitted', { surface: source });
          if (dock && card.hidden && !panelOpen()) dock.hidden = true;
          setState('success');
        } else {
          setState('idle');
          showError(scope, message);
        }
      }, wait);
    }

    subscribe(email, source)
      .then(function (r) {
        // 202 is the documented success. Anything else is a real failure.
        if (r.status === 202 || r.ok) {
          // Record it the moment the answer arrives, not after the half second
          // the spinner is held for. If anything tears the page down in
          // between, they are still marked as joined and never asked again.
          write({ joined: true });
          finish(true);
          return;
        }
        // The customer gets one plain sentence; the real reason goes to the
        // console, because a silent generic failure is how a broken payload
        // shape went unnoticed in the first place.
        r.text().then(function (body) {
          if (window.console && console.warn) console.warn('[email-capture] Klaviyo ' + r.status + ': ' + body);
        }).catch(function () {});
        finish(false, 'We could not add that address. Please check it and try again.');
      })
      .catch(function () { finish(false, 'Something went wrong. Please try again.'); });
  });

  /* ---------- boot, and re-boot whenever the editor swaps the section ------- */
  function boot() {
    if (!bind()) return;
    start();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && !e.target.querySelector('[data-ec-root]')) return;
    editorClosed = false;
    boot();
  });
})();
